import { LifeBuoy, BookOpen, MessageCircle, Mail, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";

export default function Support() {
  return (
    <div className="space-y-6 animate-in" data-testid="support-page">
      <div>
        <h2 className="font-heading text-2xl font-bold text-forest flex items-center gap-2">
          <LifeBuoy className="h-6 w-6" />
          Support & Help
        </h2>
        <p className="text-sage text-sm">User guides and developer contact information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Guide */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-heading text-forest flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              User Guide
            </CardTitle>
            <CardDescription>Quick reference for using TherapyM</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-sand rounded-lg">
                <h4 className="font-medium text-forest text-sm">Daily Booking</h4>
                <p className="text-xs text-sage mt-1">Create client bookings by selecting branch, therapist, service, and time slot. Walk-in and booked appointments are supported.</p>
              </div>
              <div className="p-3 bg-sand rounded-lg">
                <h4 className="font-medium text-forest text-sm">Operations Log</h4>
                <p className="text-xs text-sage mt-1">View, edit, and delete all daily transactions. Filter by date and branch.</p>
              </div>
              <div className="p-3 bg-sand rounded-lg">
                <h4 className="font-medium text-forest text-sm">Schedule</h4>
                <p className="text-xs text-sage mt-1">Visual timetable showing therapist columns and time slot rows. Only rostered staff appear.</p>
              </div>
              <div className="p-3 bg-sand rounded-lg">
                <h4 className="font-medium text-forest text-sm">Roster</h4>
                <p className="text-xs text-sage mt-1">Weekly roster grid auto-populated from staff profiles. Add or remove staff from specific days.</p>
              </div>
              <div className="p-3 bg-sand rounded-lg">
                <h4 className="font-medium text-forest text-sm">Accounting</h4>
                <p className="text-xs text-sage mt-1">Daily cash flow tracker. Edit opening cash manually, add expenses, and set safe deposits.</p>
              </div>
              <div className="p-3 bg-sand rounded-lg">
                <h4 className="font-medium text-forest text-sm">Staff & Services</h4>
                <p className="text-xs text-sage mt-1">Manage therapists (with regular roster and super rates) and service pricing (with staff wages).</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Developer */}
        <Card className="border-border border-forest/20">
          <CardHeader>
            <CardTitle className="font-heading text-forest flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Contact Developer
            </CardTitle>
            <CardDescription>Report issues or request new features</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-sage">
              Having trouble or need a new feature? Reach out directly through any of the channels below.
            </p>

            <div className="space-y-3">
              {/* LINE Contact */}
              <a
                href="https://line.me/ti/p/~psalyim"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-[#06C755]/5 border border-[#06C755]/20 rounded-xl hover:bg-[#06C755]/10 transition-colors group"
                data-testid="contact-line"
              >
                <div className="w-10 h-10 bg-[#06C755] rounded-xl flex items-center justify-center shrink-0">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-forest text-sm">LINE</p>
                  <p className="text-xs text-sage">ID: psalyim</p>
                </div>
                <ExternalLink className="h-4 w-4 text-sage group-hover:text-forest transition-colors" />
              </a>

              {/* Email Contact */}
              <a
                href="mailto:memc.yim@gmail.com"
                className="flex items-center gap-3 p-4 bg-terracotta/5 border border-terracotta/20 rounded-xl hover:bg-terracotta/10 transition-colors group"
                data-testid="contact-email"
              >
                <div className="w-10 h-10 bg-terracotta rounded-xl flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-forest text-sm">Email</p>
                  <p className="text-xs text-sage">memc.yim@gmail.com</p>
                </div>
                <ExternalLink className="h-4 w-4 text-sage group-hover:text-forest transition-colors" />
              </a>
            </div>

            <div className="pt-3 border-t border-border">
              <p className="text-xs text-sage">
                When reporting an issue, please include the page name, what you were doing, and what went wrong. Screenshots are very helpful!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
